import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService, OauthType, Signup, USER_EMAIL_VERIFIED, UsersService } from '@flashvpn-io/web-core';
import { APIManager } from '@flashvpn-io/web-core';
import { User } from '@flashvpn-io/web-core';
import { WebUIService } from '../../web-ui.service';
import { ToastService } from '../../components/ui/toast/toast.service';
import { AppConfigService } from '../../services/app-config.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css'],
})
export class AuthComponent implements OnInit {
  protected readonly OauthType = OauthType;
  protected activeOauthType: OauthType = OauthType.SIGNIN;

  submitted: any;
  redirect: string = '';
  queryParams: object = {};
  protected showRegistration = false;
  showForgotPassword = false;
  signupData: { email: string } | null = null;
  time = 0;
  public isExtension = false;
  protected isCodeSent = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private usersService: UsersService,
    public appService: WebUIService,
    private notificationService: NotificationService,
    private apiManager: APIManager,
    private http: HttpClient,
    private toastService: ToastService,
    private appConfigService: AppConfigService,
    private translate: TranslateService
  ) {
    this.isExtension = this.appConfigService.getBuildTarget() === 'extension';
  }

  async ngOnInit() {
    this.route.url.subscribe(async segments => {
      if (await this.usersService.isLoggedIn()) {
        this.loginRedirect();
      }
      const path = segments[0]?.path;
      switch (path) {
        case 'signup':
          this.activeOauthType = OauthType.SIGNUP;
          this.showRegistration = false;
          this.showForgotPassword = false;
          break;
        case 'forgot':
          this.showForgotPassword = true;
          break;
        case 'verify':
          this.activeOauthType = OauthType.SIGNIN;
          this.showRegistration = true;
          this.showForgotPassword = false;
          break;
        default:
          this.activeOauthType = OauthType.SIGNIN;
          this.showRegistration = false;
          this.showForgotPassword = false;
      }
    });
    this.initTranslations();
  }

  private initTranslations(): void {
    this.translate.setDefaultLang('zh-CN');
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang) {
      this.translate.use(savedLang);
    } else {
      const browserLang = this.translate.getBrowserLang();
      const lang = browserLang?.match(/zh|en|ru/) ?
        (browserLang === 'zh' ? 'zh-CN' : browserLang === 'en' ? 'en-US' : 'ru-RU') :
        'zh-CN';
      this.translate.use(lang);
      localStorage.setItem('selectedLanguage', lang);
    }
  }

  setChatwootDefaultUserInfo(user: User) {
    // @ts-ignore
    window.$chatwoot.setUser(user.id, {
      name: user.email, // Name of the user
      email: user.email, // Email of the user
      country_code: user.locale, // Two letters country code
    });
  }

  navigateToLogin() {
    this.router.navigate(['auth', 'signin']);
  }

  navigateToSignup() {
    this.router.navigate(['auth', 'signup']);
  }

  navigateToForgotPassword() {
    this.router.navigate(['auth', 'forgot']);
  }

  navigateToVerify() {
    this.router.navigate(['auth', 'verify']);
  }

  onLogin(event: any) {
    this.submitted = true;
    this.apiManager.login(event.email, event.password).subscribe(
      async (user) => {
        localStorage.setItem('refresh_token', (user as any)?.refreshToken);
        localStorage.setItem('token', (user as any)?.token);
        await this.usersService.loginByUser(user as User);

        if ((user as any).emailVerified === 1) {
          this.loginRedirect();
        } else {
          this.signupData = { email: event.email };
          this.showRegistration = true;
          this.submitted = false;
          this.onResend();
        }
      },
      async (res) => {
        this.submitted = false;
        if (res.error) {
          this.toastService.error(res.error.message);
        }
      }
    );
  }

  onSignup(event: any) {
    const utmSource = this.route.snapshot.queryParams['utm_source'] || localStorage.getItem("utmSource") || undefined;
    const utmMedium = this.route.snapshot.queryParams['utm_medium'] || localStorage.getItem("utmMedium") || undefined;
    const utmCampaign = this.route.snapshot.queryParams['utm_campaign'] || localStorage.getItem("utmCampaign") || undefined;
    const utmTerm = this.route.snapshot.queryParams['utm_term'] || localStorage.getItem("utmTerm") || undefined;
    const utmContent = this.route.snapshot.queryParams['utm_content'] || localStorage.getItem("utmContent") || undefined;
    const registerInfo: Signup = {
      email: event.email,
      password: event.password,
      inviterCode: undefined,
      utmSource,
      utmMedium,
      utmCampaign,
      utmTerm,
      utmContent,
    };
    this.submitted = true;
    this.apiManager.createUser(registerInfo).subscribe(
      async (user) => {
        localStorage.setItem("refresh_token", (user as any)?.refreshToken);
        this.setChatwootDefaultUserInfo(user as User);
        await this.usersService.loginByUser(user as User);
        if ((user as any).emailVerified === 1) {
          await this.router.navigate(["services", "dashboard"]);
        } else {
          this.signupData = { email: event.email };
          this.showRegistration = true;
        }
      },
      async (res) => {
        this.submitted = false;
        if (res.error) {
          this.toastService.error(res.error.message);
        }
      }
    );
  }

  onVerifyEmail(event: any) {
    this.submitted = true;
    const email = this.signupData?.email || this.usersService?.user?.email;
    if (!email) {
      console.error("Email is undefined");
      return;
    }
    this.http.get(`${this.apiManager.serverUrl}/api/v1/users/verify/${event.code}/${email}`).subscribe(
      async (data: any) => {
        await this.usersService.login(data["token"], email);
        this.notificationService.post(USER_EMAIL_VERIFIED, true);
        await this.router.navigate(["apps"]);
      },
      (res: { error: { message: string; }; }) => {
        this.submitted = false;
        this.toastService.error(res.error.message);
      }
    );
  }

  onResend() {
    if (this.submitted) {
      return;
    }
    this.submitted = true;
    this.apiManager.resendEmail().subscribe(
      (_) => {
        this.toastService.success(this.translate.instant('Auth.form.resendEmailSuccess'));
        this.time = 60;
        const timer = setInterval(() => {
          if (this.time > 0) {
            this.time--;
          } else {
            clearInterval(timer);
          }
        }, 1000);
      },
      (res) => {
        this.toastService.error(res?.error?.message);
      },
      () => (this.submitted = false)
    );
  }

  onSendResetCode(data: { email: string }) {
    console.log('called');
    this.apiManager.sendResetPasswordEmail(data.email).subscribe({
      next: () => {
        this.toastService.success(this.translate.instant('Common.Success.codeSent'));
        this.isCodeSent = true;
      },
      error: (err) => {
        this.toastService.error(err.error.message);
      }
    })
  }

  async loginRedirect() {
    if (this.isExtension) {
      await this.router.navigate(['home']);
    } else {
      await this.router.navigate(['services', 'dashboard']);
    }
  }

  onResetPassword(data: { email: string, code: string, newPassword: string }) {
    this.apiManager.resetPassword(data.code, data.newPassword).subscribe({
      next: () => {
        this.toastService.success(this.translate.instant('Common.Success.passwordReset'), 5000);
        this.navigateToLogin();
      },
      error: (error: any) => {
        this.toastService.error(error.error.message);
      }
    });
  }
}
